import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, NG_VALIDATORS, Validator} from "@angular/forms";
import {TranslocoService} from "@ngneat/transloco";
import {AbstractComponent} from "../abstract.component";
import {Lookups} from "@model";
import {CodebookService} from "@services";
import {required} from '@utility';
import {ActivatedRoute} from '@angular/router';

@Component({
  selector: 'app-language-input',
  templateUrl: './language-input.component.html',
  styleUrls: ['./language-input.component.scss'],
  providers:
    [{
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: LanguageInputComponent
    }]
})
export class LanguageInputComponent extends AbstractComponent implements OnInit, OnChanges {
  @Input() inputForm?: FormGroup<any>;
  @Input() defaultLanguage?: number | undefined;
  @Input() extraLanguages?: number[] | undefined;
  @Input() required: boolean = true;
  @Input() hrKey?: string;
  @Input() enKey?: string;
  @Input() siKey?: string;
  @Input() itKey?: string;
  @Input() srKey?: string;
  @Input() bsKey?: string
  @Input() hrLabel?: string;
  @Input() enLabel?: string;
  @Input() siLabel?: string;
  @Input() itLabel?: string;
  @Input() srLabel?: string;
  @Input() bsLabel?: string;
  @Input() isInput: boolean = true;
  @Input() isRichText: boolean = false;

  selectedLanguage: string = "hr";
  languages: Lookups[] = [];
  defaultLanguageCode: string = "hr";
  richTextContent: string = "";
  disabled: boolean = false;

  constructor(public translocoService: TranslocoService, private codebookService: CodebookService, private route: ActivatedRoute) {
    super();
  }

  ngOnInit(): void {
    this.initCodebookService();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['defaultLanguage']?.currentValue !== changes['defaultLanguage']?.previousValue) {
      this.setRequiredMultiLanguageFields();
    }
    if (JSON.stringify(changes['extraLanguages']?.currentValue) !== JSON.stringify(changes['extraLanguages']?.previousValue)) {
      this.setRequiredMultiLanguageFields();
    }
    if (changes['inputForm']?.currentValue !== changes['inputForm']?.previousValue) {
      this.setRequiredMultiLanguageFields();
    }
  }

  private initCodebookService() {
    this.subscribeToCodebookServiceValues()
  }

  private subscribeToCodebookServiceValues(): void {
    // todo - check if getting from route data is possible
    this.subscriptions.push(this.codebookService.languages.value.subscribe((value: Lookups[] | null) => {
      if (value !== null) {
        this.languages = value;
        this.setRequiredMultiLanguageFields();
      }
    }));
  }


  private setRequiredMultiLanguageFields() {
    let defaultLang;
    if (this.defaultLanguage) {
      defaultLang = this.languages.filter((language: Lookups) => language?.id === this.defaultLanguage)[0];
    } else {
      defaultLang = this.languages.filter((language: Lookups) => language?.code === this.translocoService.getActiveLang())[0];
    }
    this.setLanguage(defaultLang?.code ?? "hr")
    this.defaultLanguageCode = defaultLang?.code ?? "hr";

    if (this.required) {
      let requiredFormControl: FormControl | undefined = this.getLanguageFormControl(defaultLang?.code)
      let nonRequiredFields: FormControl[] = this.getNonRequiredLanguageNameFields(defaultLang?.code)
      if (requiredFormControl) {
        requiredFormControl.addValidators([required]);
        requiredFormControl.updateValueAndValidity();
      }
      nonRequiredFields.forEach((control: FormControl) => {
        control.removeValidators([required]);
        control.updateValueAndValidity();
      });
      if (this.extraLanguages) {
        this.extraLanguages.forEach((extraLanguageId: number) => {
          let extraLanguage = this.languages.filter((language: Lookups) => language?.id === extraLanguageId!)[0];
          let extraLanguageFormControl = this.getLanguageFormControl(extraLanguage?.code);
          if (extraLanguageFormControl) {
            extraLanguageFormControl.addValidators([required]);
            extraLanguageFormControl.updateValueAndValidity();
          }
        });
      }
    }
  }

  setActiveFormControl(lang: string) {
    if (this.getLanguageFormControl(lang)?.value) {
      this.richTextContent = this.getLanguageFormControl(lang)?.value ?? ""
    }
    else {
      this.richTextContent = "";
    }
    this.disabled = this.getLanguageFormControl(lang)?.disabled ?? false;
  }

  richTextChange(content: string | undefined) {
    this.getLanguageFormControl(this.selectedLanguage)?.setValue(content);
  }


  private getLanguageFormControl(lang: string | undefined): FormControl | undefined {
    switch (lang) {
      case "hr":
        // @ts-ignore
        return this.inputForm!.controls[this.hrKey!];
      case "en":
        // @ts-ignore
        return this.inputForm!.controls[this.enKey!];
      case "it":
        // @ts-ignore
        return this.inputForm!.controls[this.itKey!];
      case "slo":
        // @ts-ignore
        return this.inputForm!.controls[this.siKey!];
      case "sr":
        // @ts-ignore
        return this.inputForm!.controls[this.srKey!];
      case "bs":
        // @ts-ignore
        return this.inputForm!.controls[this.bsKey!];
      default:
        return undefined;
    }
  }

  private getNonRequiredLanguageNameFields(lang: string | undefined): FormControl[] {
    switch (lang) {
      case "hr":
        return [
          // @ts-ignore
          this.inputForm!.controls[this.enKey!],
          // @ts-ignore
          this.inputForm!.controls[this.itKey!],
          // @ts-ignore
          this.inputForm!.controls[this.siKey!],
        // @ts-ignore
          this.inputForm!.controls[this.srKey!],
        // @ts-ignore
        this.inputForm!.controls[this.bsKey!]];
      case "en":
        return [
          // @ts-ignore
          this.inputForm!.controls[this.hrKey!],
          // @ts-ignore
          this.inputForm!.controls[this.itKey!],
          // @ts-ignore
          this.inputForm!.controls[this.siKey!],
        // @ts-ignore
        this.inputForm!.controls[this.srKey!],
        // @ts-ignore
        this.inputForm!.controls[this.bsKey!]];
      case "it":
        return [
          // @ts-ignore
          this.inputForm!.controls[this.hrKey!],
          // @ts-ignore
          this.inputForm!.controls[this.enKey!],
          // @ts-ignore
          this.inputForm!.controls[this.siKey!],
        // @ts-ignore
        this.inputForm!.controls[this.srKey!],
        // @ts-ignore
        this.inputForm!.controls[this.bsKey!]];
      case "slo":
        return [
          // @ts-ignore
          this.inputForm!.controls[this.hrKey!],
          // @ts-ignore
          this.inputForm!.controls[this.itKey!],
          // @ts-ignore
          this.inputForm!.controls[this.enKey!],
        // @ts-ignore
          this.inputForm!.controls[this.srKey!],
        // @ts-ignore
        this.inputForm!.controls[this.bsKey!]];
      case "sr":
        return [
          // @ts-ignore
          this.inputForm!.controls[this.hrKey!],
          // @ts-ignore
          this.inputForm!.controls[this.itKey!],
          // @ts-ignore
          this.inputForm!.controls[this.enKey!],
        // @ts-ignore
        this.inputForm!.controls[this.siKey!],
        // @ts-ignore
        this.inputForm!.controls[this.bsKey!]];
        case "bs":
        return [
          // @ts-ignore
          this.inputForm!.controls[this.hrKey!],
          // @ts-ignore
          this.inputForm!.controls[this.itKey!],
          // @ts-ignore
          this.inputForm!.controls[this.enKey!],
        // @ts-ignore
        this.inputForm!.controls[this.siKey!]];
      default:
        return []
    }
  }

  setLanguage(lang: string): void {
    this.selectedLanguage = lang;
    if (this.isRichText) {
      this.setActiveFormControl(lang);
    }

  }

}
